<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import type { Router } from "vue-router";
import { debounce } from "../../../legl-ui/utils";
import TableNoResultsIcon from "../../fancy-icons/TableNoResultsIcon.vue";
import FetchFailed from "./components/FetchFailed.vue";
import Loading from "./components/Loading.vue";
import TableMessage from "./components/TableMessage.vue";

import "../../../apps/lawyers-app/static-src/js/components/new-tables/components/filters/lawyers-table-select-filter";
import "../../../apps/lawyers-app/static-src/js/components/new-tables/components/lawyers-table-chips";
import "../../../apps/lawyers-app/static-src/js/components/new-tables/components/lawyers-table-search";
interface Props {
  nextUrl?: string | null;
  previousUrl?: string | null;
  recordCount?: number;
  totalRecordCount?: number;
  hasRecords?: boolean;
  isLoading?: boolean;
  fetchFailed?: boolean;
  loadingMessage?: string;
  noResultsTitle?: string;
  noResultsMessage?: string;
  searchPlaceholder?: string;
  filterConfig?: Array<any>;
  isSearchable?: boolean;
  withPagination?: boolean;
  router?: Router;
  APIUrl?: string;
  updateURL?: Function;
  persistedQueryParam?: string | null;
}

type EventTarget = { target: HTMLInputElement & { modelValue: any } };
type HTMLEventType = Event & EventTarget;

const props = withDefaults(defineProps<Props>(), {
  searchPlaceholder: "Search",
  isSearchable: true,
  withPagination: true,
  hasRecords: true,
  noResultsMessage:
    "Please try editing or removing any applied filters on the table.",
  noResultsTitle: "No results found",
  persistedQueryParam: null,
});

const searchValue = ref<string>("");

onBeforeMount(async () => {
  searchValue.value = props.router?.currentRoute.value.query.search
    ? props.router?.currentRoute.value.query.search.toString()
    : "";
});

const emit = defineEmits<(e: "fetch:tableResults") => void>();

const limit = ref<string>();

const appliedFilters = ref<{ filters: { [key: string]: string[] } }>({
  filters: {},
});

async function updateTable(newQueryParams) {
  await props.router?.push({
    path: window.location.pathname,
    query: newQueryParams,
  });
}

function clearFilters(): void {
  if (!props.router && props.updateURL) {
    const emptyFilters = {};
    props.filterConfig?.forEach((filter) => {
      emptyFilters[filter.data] = [];
    });
    appliedFilters.value = {
      filters: emptyFilters,
    };
  } else {
    const queryParams = new URLSearchParams(window.location.search);
    const newQueryParams = { limit: queryParams.get("limit") };
    updateTable(newQueryParams);
  }
  searchValue.value = "";
}

watch(
  () => props.router?.currentRoute.value.query,
  () => {
    limit.value = props.router?.currentRoute.value.query.limit
      ? props.router?.currentRoute.value.query.limit.toString()
      : "10";
    const newAppliedFilters = { filters: {} };

    props.filterConfig?.forEach((filter) => {
      newAppliedFilters.filters[filter.data] = [];

      let filterValue =
        props.router?.currentRoute.value.query[filter.queryParamKey];
      if (
        typeof filterValue === "string" &&
        filterValue &&
        filterValue !== ""
      ) {
        filterValue = filterValue.split(",");
        newAppliedFilters.filters[filter.data] = filterValue;
      }
    });
    appliedFilters.value = newAppliedFilters;
    searchValue.value = props.router?.currentRoute.value.query.search
      ? props.router?.currentRoute.value.query.search.toString()
      : "";
  },
  {
    immediate: true,
    deep: true,
  },
);

const debouncedUpdateTable = debounce((term) => {
  if (!props.router && props.updateURL) {
    const { currentAPIUrlPathname, queryParams } = splitApiUrl();
    queryParams.set("search", term);
    queryParams.delete("offset");
    props.updateURL(`${currentAPIUrlPathname}?${queryParams.toString()}`);
  } else {
    const newQueryParams: { offset?: string; search: string } = {
      ...props.router?.currentRoute.value.query,
      search: term,
    };
    delete newQueryParams.offset;
    updateTable(newQueryParams);
  }
}, 200);

const onSearch = (term) => {
  searchValue.value = term;
  debouncedUpdateTable(term);
};

function changePage(url: string | null | undefined): void {
  if (!url) {
    return;
  }
  if (!props.router && props.updateURL) {
    const newURL = new URL(url);
    props.updateURL(`${newURL.pathname}${newURL.search}`);
  } else {
    const apiUrl = new URL(url);
    const queryParams = new URLSearchParams(apiUrl.search);
    const currentQuery = props.router?.currentRoute.value.query;

    if (currentQuery?.search) {
      queryParams.set("search", currentQuery.search.toString());
    }

    updateTable(Object.fromEntries(queryParams.entries()));
  }
}

function splitApiUrl() {
  const currentAPIUrl = props.APIUrl && props.APIUrl.split("?");
  const currentAPIUrlPathname = currentAPIUrl && currentAPIUrl[0];
  const currentAPIUrlSearchParams = currentAPIUrl && currentAPIUrl[1];
  const queryParams = new URLSearchParams(currentAPIUrlSearchParams);
  return { currentAPIUrlPathname, queryParams };
}

function changeLimit(event: HTMLEventType): void {
  if (!props.router && props.updateURL) {
    const newLimit = event.target.modelValue;
    const { currentAPIUrlPathname, queryParams } = splitApiUrl();
    queryParams.set("limit", newLimit);
    queryParams.delete("offset");
    props.updateURL(`${currentAPIUrlPathname}?${queryParams.toString()}`);
  } else {
    limit.value = event.target.modelValue;
    const queryParams = new URLSearchParams(window.location.search);
    if (limit.value) {
      queryParams.set("limit", limit.value);
    }
    queryParams.delete("offset");
    updateTable(Object.fromEntries(queryParams.entries()));
  }
}

function recordNumberDisplay() {
  let queryParams;
  if (!props.router) {
    const apiUrlObject = splitApiUrl();
    queryParams = apiUrlObject.queryParams;
  } else {
    queryParams = new URLSearchParams(window.location.search);
  }
  let firstOffsetRecord = Number.parseInt(queryParams.get("offset") || "1");

  if (props.recordCount === 0) {
    firstOffsetRecord = 0;
  }
  // remove 0-indexing
  if (firstOffsetRecord !== 1) {
    firstOffsetRecord = firstOffsetRecord + 1;
  }
  const lastOffsetRecord = props.recordCount
    ? firstOffsetRecord - 1 + props.recordCount
    : firstOffsetRecord;
  if (props.totalRecordCount) {
    return `${firstOffsetRecord} - ${lastOffsetRecord} of ${props.totalRecordCount}`;
  } else {
    return `${firstOffsetRecord} - ${lastOffsetRecord}`;
  }
}

const chips = computed(() => {
  const chips: { label: string; value: string; column: string }[] = [];
  for (const [key, filter] of Object.entries(appliedFilters.value.filters)) {
    const filterConfig = props.filterConfig?.find(
      (filter) => filter.data === key,
    );
    filter?.forEach((value) => {
      const filterOption = filterConfig?.options.find(
        (option) => option.value == value,
      );
      if (filterOption) {
        const label = `${filterConfig.label}: ${filterOption.label}`;
        chips.push({ label, value, column: key });
      }
    });
  }
  return chips;
});

function removeFilter(column, value) {
  appliedFilters.value = {
    ...appliedFilters.value,
    filters: {
      ...appliedFilters.value.filters,
      [column]: [
        ...appliedFilters.value.filters[column].filter((x) => x !== value),
      ],
    },
  };
}

function getPersistedQueryParam(queryParams: Record<string, any>): {
  [key: string]: string;
} {
  if (!props.persistedQueryParam) {
    return {};
  }
  return {
    [props.persistedQueryParam]: queryParams.get(props.persistedQueryParam)!,
  };
}

const onFilter = (column) => {
  return (filter) => {
    if (filter.checked == true) {
      if (!appliedFilters.value.filters[column].includes(filter.id)) {
        appliedFilters.value = {
          ...appliedFilters.value,
          filters: {
            ...appliedFilters.value.filters,
            [column]: [...appliedFilters.value.filters[column], filter.id],
          },
        };
      }
    } else {
      removeFilter(column, filter.id);
    }
    if (!props.router && props.updateURL) {
      const { currentAPIUrlPathname, queryParams } = splitApiUrl();
      props.filterConfig?.forEach((filter) => {
        const filterValue = appliedFilters.value.filters[filter.data].join(",");
        queryParams.set(filter.queryParamKey, filterValue);
      });
      queryParams.delete("offset");
      props.updateURL(`${currentAPIUrlPathname}?${queryParams.toString()}`);
    } else {
      const queryParams = new URLSearchParams(window.location.search);

      const newQueryParams: {
        limit: string;
        offset?: string;
        search?: string;
        ordering?: string;
      } = {
        ...getPersistedQueryParam(queryParams),
        limit: queryParams.get("limit")!,
      };

      // We need to get the hash from the url to enable the drawer to open
      const hash = window.location.hash;

      if (queryParams.has("search")) {
        newQueryParams.search = queryParams.get("search")!;
      }

      if (queryParams.has("ordering")) {
        newQueryParams.ordering = queryParams.get("ordering")!;
      }
      props.filterConfig?.forEach((filter) => {
        const filterValue = appliedFilters.value.filters[filter.data].join(",");
        if (filterValue && filterValue !== "") {
          newQueryParams[filter.queryParamKey] = filterValue;
        }
      });
      props.router?.push({
        path: window.location.pathname,
        query: newQueryParams,
        hash: hash,
      });
    }
  };
};

function handleSort({
  column,
  direction,
}: {
  column: string;
  direction: string;
}) {
  // Compose new query params for sorting
  const queryParams = new URLSearchParams(window.location.search);
  queryParams.set("ordering", direction === "desc" ? `-${column}` : column);

  // Preserve other params (e.g., filters, search)
  props.router?.push({
    path: window.location.pathname,
    query: Object.fromEntries(queryParams.entries()),
    hash: window.location.hash,
  });
}
</script>

<template>
  <div class="table-wrapper" data-cy-legl-table data-cy-engage-admin-table>
    <slot name="bulk-actions"></slot>
    <div class="table-inner-wrapper">
      <slot name="table-intro"></slot>
      <template v-if="hasRecords">
        <legl-lawyers-table-chips
          v-if="filterConfig"
          :clickHandler="removeFilter"
          :chips="chips"
        ></legl-lawyers-table-chips>
        <div class="filter-wrapper">
          <legl-lawyers-table-search
            v-if="isSearchable"
            class="search-input"
            :value="searchValue"
            :onSearch.prop="onSearch"
            :searchPlaceholder="searchPlaceholder"
          ></legl-lawyers-table-search>
          <template v-if="filterConfig">
            <template v-for="filter in filterConfig" :key="filter.data">
              <legl-lawyers-table-select-filter
                v-show="!filter.hidden"
                :data-testid="`filter-${filter.data}`"
                :data-test="`filter-${filter.data}`"
                :label="filter.label"
                :data="filter.data"
                :options="filter.options"
                :onFilter.prop="onFilter(filter.data)"
                :appliedFilters="appliedFilters"
                class="select-filter"
              ></legl-lawyers-table-select-filter>
            </template>
          </template>

          <slot name="additional-actions"></slot>
        </div>
      </template>
      <div>
        <div v-if="hasRecords" class="content-wrapper">
          <table>
            <thead data-testid="table-header">
              <tr>
                <slot
                  class="table-head"
                  name="table-head"
                  :onSort="handleSort"
                ></slot>
              </tr>
            </thead>
            <tbody v-if="!isLoading" data-testid="table-body">
              <slot class="table-body" name="table-body"></slot>
            </tbody>
          </table>
        </div>
        <slot class="feedback" name="feedback">
          <Loading v-if="isLoading" :message="loadingMessage" />
          <TableMessage
            v-if="
              !isLoading &&
              (searchValue || chips.length) &&
              totalRecordCount === 0 &&
              !fetchFailed
            "
            :title="noResultsTitle"
            :message="noResultsMessage"
          >
            <template #icon>
              <TableNoResultsIcon />
            </template>
            <lds-button
              v-if="filterConfig"
              @click="clearFilters"
              colour="primary"
              variant="outlined"
              >Clear filters</lds-button
            >
          </TableMessage>
          <FetchFailed
            v-if="fetchFailed && !isLoading"
            @refetch:tableResults="emit('fetch:tableResults')"
          />
        </slot>
      </div>
      <div v-if="withPagination && recordCount" class="table-footer">
        <div class="table-footer-item rows-select">
          <div class="footer-text">
            <label>Rows per page</label>
          </div>
          <div>
            <lds-select
              label="Rows per page"
              :modelValue="limit"
              @model-value-changed="changeLimit"
              :small="true"
              u-sr-only
            >
              <select slot="input">
                <option :value="10" :key="10">10</option>
                <option :value="25" :key="25">25</option>
                <option :value="50" :key="50">50</option>
                <option :value="100" :key="100">100</option>
              </select>
            </lds-select>
          </div>
        </div>
        <div class="table-footer-item footer-text">
          <span>{{ recordNumberDisplay() }}</span>
        </div>
        <div class="table-footer-item">
          <lds-icon-button
            colour="neutral"
            variant="text"
            name="lds-icon-ChevronLeft"
            :disabled="!previousUrl"
            @click.prevent="changePage(previousUrl)"
          />
          <lds-icon-button
            colour="neutral"
            variant="text"
            name="lds-icon-ChevronRight"
            :disabled="!nextUrl"
            @click.prevent="changePage(nextUrl)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.table-footer {
  padding-top: $spacing-s;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0 $spacing-s;
  color: $color-neutral-700;
}

.rows-select {
  display: flex;
  align-items: center;
  gap: 0 $spacing-s;
}

.rows-select lds-select {
  width: 66px;
}
.select-filter {
  margin-right: 24px;
}
.table-wrapper {
  margin-bottom: $spacing-m;
  background-color: white;
  overflow: auto;
  border-radius: 4px;
  @include typography-medium-normal;
  color: $color-neutral-800;
  table tbody :slotted(tr td) {
    padding: $spacing-s;
    vertical-align: middle;
    color: $color-neutral-800;
  }
}

.table-inner-wrapper {
  padding: $spacing-m;
  @media screen  and (max-width: 768px) {
    padding: 0;
  }
}


.content-wrapper {
  width: 100%;
  overflow: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

table :slotted(tr),
tr {
  border-bottom: 1px solid $color-neutral-200;
}

table thead {
  height: 70px;
}

table tbody :slotted(tr) {
  min-height: 100px;
}

table thead tr :slotted(th) {
  padding: $spacing-s;
  text-align: left;
  color: $color-neutral-800;
  @include typography-body-bold;
}

.filter-wrapper {
  display: flex;
  column-gap: 16px;
  row-gap: 16px;
  flex-wrap: wrap;
}

table tbody :slotted(tr.is-clickable:hover) {
  background: $color-neutral-100;
  cursor: pointer;
}

.search-input {
  flex-grow: 2;
}

.footer-text {
  @include typography-small-normal
}

@media screen and (max-width: 480px) {
  .footer-text {
    border: 0;
    clip: rect(1px 1px 1px 1px);
    /* IE6, IE7 */
    clip: rect(1px, 1px, 1px, 1px);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
  }

  .table-footer {
    justify-content: space-between;
  }
}
</style>
